import { useParams } from "react-router-dom";
import {
  cancelModuSignDocumentAsync,
  getModuSignDocumentDetailAsync,
  getModuSignDocumentListAsync,
  getModuSignDocumentPreviewAsync,
  getModuSignTemplateListAsync,
  getModuSignTemplatePreviewAsync,
  postModuSignRequestAsync,
  resendModuSignDocumentAsync,
} from "src/api/contract/modusign-api";
import { ModuSignCancelDocument, ModuSignStartDocumentRequest } from "src/api/contract/modusign-types";
import { useApiOperation } from "src/api/hooks";

const useModuSign = () => {
  const { id } = useParams();

  // API 실행 함수들
  const { executeAsync: executeGetModuSignTemplateList } = useApiOperation(getModuSignTemplateListAsync);
  const { executeAsync: executeGetModuSignTemplatePreview } = useApiOperation(getModuSignTemplatePreviewAsync);
  const { executeAsync: executeGetModuSignDocumentList } = useApiOperation(getModuSignDocumentListAsync, { noErrorModal: true });
  const { executeAsync: executePostModuSignRequest } = useApiOperation(postModuSignRequestAsync);
  const { executeAsync: executeCancelModuSignDocument } = useApiOperation(cancelModuSignDocumentAsync);
  const { executeAsync: executeGetModuSignDocumentPreview } = useApiOperation(getModuSignDocumentPreviewAsync);
  const { executeAsync: executeResendModuSignDocument } = useApiOperation(resendModuSignDocumentAsync);
  const { executeAsync: executeGetModuSignDocumentDetail } = useApiOperation(getModuSignDocumentDetailAsync);

  // RCS50. 전자계약 서식 목록 조회(모두싸인)
  const fetchModuSignTemplateList = async () => {
    const response = await executeGetModuSignTemplateList(id);
    return response.data.data.templateList;
  };

  // RCS51. 전자계약 서식 상세 조회(모두싸인)
  const fetchModuSignTemplatePreview = async (templateId: string) => {
    const response = await executeGetModuSignTemplatePreview({ contractId: id!, templateId });
    return response.data.data.templateInfo;
  };

  // RCS61. 전자계약 문서 서명요청(모두싸인) (저장)
  const onSaveModuSignDocument = async (data: ModuSignStartDocumentRequest) => {
    const response = await executePostModuSignRequest({ contractId: id!, startDocument: data });
    return response.data.data;
  };

  // RCS55. 전자계약 문서 목록 조회(모두싸인)
  const fetchModuSignDocumentList = async () => {
    const response = await executeGetModuSignDocumentList({ contractId: id! });
    const errorCode = (response as any)?.data?.meta?.errorCode;
    if (errorCode === "eMS429") {
      const retryAfter = (response as any)?.data?.meta?.errorData?.retryAfter;
      throw Object.assign(new Error("모두싸인 API 최대 허용 요청 수 초과"), { errorCode: "eMS429", retryAfter });
    }
    return response.data.data.documentList;
  };

  // RCS63. 진행 중인 문서 서명요청 취소(모두싸인)
  const onCancelModuSignDocument = async (moduSignCancelDocument: ModuSignCancelDocument) => {
    const response = await executeCancelModuSignDocument({ contractId: id!, cancelDocument: moduSignCancelDocument });
    return response.data.data;
  };

  // RCS62. 전자계약 문서 미리보기(모두싸인)
  const fetchModuSignDocumentPreview = async (moduSignStartDocument: ModuSignStartDocumentRequest) => {
    const response = await executeGetModuSignDocumentPreview({ contractId: id!, startDocument: moduSignStartDocument });
    return response.data.data;
  };

  // RCS65. 진행 중인 문서 재전송(모두싸인)
  const resendModuSignDocument = async (documentId: string) => {
    const response = await executeResendModuSignDocument({ contractId: id!, documentId });
    return response.data.data;
  };

  // RCS56. 전자계약 문서 상세 조회(모두싸인)
  const openModuSignDocumentDetail = async (documentId: string) => {
    const response = await executeGetModuSignDocumentDetail({ contractId: id!, documentId });
    return response.data.data.documentInfo;
  };

  return {
    fetchModuSignTemplateList,
    fetchModuSignTemplatePreview,
    onSaveModuSignDocument,
    fetchModuSignDocumentList, //
    onCancelModuSignDocument,
    fetchModuSignDocumentPreview,
    resendModuSignDocument,
    openModuSignDocumentDetail,
  };
};

export default useModuSign;
